// Shared brand + status components used across the whole app.
// Keeps the visual language (logos, status colours) consistent everywhere
// so the user can scan any page and instantly know:
//   • which social network a card belongs to (logo + color),
//   • whether an integration is connected / configured / missing / errored,
//   • where this is VendeIA-app-native content (yellow VendeIA mark).

import {
  InstagramLogo,
  FacebookLogo,
  WhatsappLogo,
  TiktokLogo,
  Globe,
  Storefront,
  CheckCircle,
  XCircle,
  WarningCircle,
  Circle,
  CircleNotch,
} from "@phosphor-icons/react";

/* ------------------------- VendeIA brand mark ----------------------------- */
export function VendeIALogo({ size = 28, withText = true, className = "" }) {
  // Pure SVG mark: a stylised "V" carved out of a klein-blue square plus a
  // yellow accent dot for the "I" — readable down to 16px.
  return (
    <span className={`inline-flex items-center gap-2 ${className}`} data-testid="vendeia-logo">
      <svg
        width={size}
        height={size}
        viewBox="0 0 40 40"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        <rect width="40" height="40" rx="6" fill="#1E4FCC" />
        <path
          d="M9 11 L20 30 L31 11"
          stroke="white"
          strokeWidth="4.5"
          strokeLinecap="square"
          strokeLinejoin="miter"
          fill="none"
        />
        <circle cx="33" cy="8" r="3.2" fill="#FFD600" />
      </svg>
      {withText && (
        <span className="font-black tracking-tight text-base leading-none">
          Vende<span className="text-klein">IA</span>
        </span>
      )}
    </span>
  );
}

/* ------------------------- Platform badge -------------------------------- */
const PLATFORM_META = {
  instagram: { label: "Instagram", icon: InstagramLogo, color: "#E1306C", bg: "#FDE7F1", border: "#F8BBD0" },
  facebook:  { label: "Facebook",  icon: FacebookLogo,  color: "#1877F2", bg: "#E3EFFE", border: "#BBDEFB" },
  whatsapp:  { label: "WhatsApp",  icon: WhatsappLogo,  color: "#25D366", bg: "#E7F8EE", border: "#A5D6A7" },
  tiktok:    { label: "TikTok",    icon: TiktokLogo,    color: "#000000", bg: "#F5F5F5", border: "#BDBDBD" },
  web:       { label: "Web",       icon: Globe,         color: "#5D4037", bg: "#EFEBE9", border: "#D7CCC8" },
  vendeia:   { label: "VendeIA",   icon: Storefront,    color: "#1E4FCC", bg: "#E7EEFF", border: "#BBDEFB" },
};

export function PlatformBadge({ platform, size = "md", showLabel = true, testid }) {
  const meta = PLATFORM_META[String(platform || "").toLowerCase()] || PLATFORM_META.vendeia;
  const Icon = meta.icon;
  const isSm = size === "sm";
  const isXs = size === "xs";
  return (
    <span
      className={`inline-flex items-center gap-1 font-bold uppercase tracking-wide ${
        isXs ? "text-[9px] px-1.5 py-0.5" : isSm ? "text-[10px] px-1.5 py-0.5" : "text-[11px] px-2 py-1"
      }`}
      style={{ color: meta.color, backgroundColor: meta.bg, border: `1px solid ${meta.border}` }}
      data-testid={testid || `platform-badge-${platform}`}
      title={meta.label}
    >
      <Icon size={isXs ? 10 : isSm ? 11 : 13} weight="fill" />
      {showLabel && meta.label}
    </span>
  );
}

/* ------------------------- Status pill ----------------------------------- */
// Unified taxonomy for integration / config / health status across the app.
export const STATUS_META = {
  connected:    { label: "Conectado",         icon: CheckCircle,  color: "#1B5E20", bg: "#E8F5E9", border: "#A5D6A7", weight: "fill" },
  configured:   { label: "Configurado",       icon: CheckCircle,  color: "#1E4FCC", bg: "#E7EEFF", border: "#BBDEFB", weight: "regular" },
  pending:      { label: "Validando…",        icon: CircleNotch,  color: "#1E4FCC", bg: "#E7EEFF", border: "#BBDEFB", weight: "bold", spin: true },
  not_configured:{label: "Sin configurar",    icon: Circle,       color: "#6B6B6B", bg: "#F5F5F5", border: "#DDDDDD", weight: "regular" },
  needs_config: { label: "Requiere config.",  icon: WarningCircle, color: "#B45309", bg: "#FFF3E0", border: "#FFCC80", weight: "fill" },
  disconnected: { label: "Desconectado",      icon: XCircle,      color: "#B71C1C", bg: "#FFEBEE", border: "#FFCDD2", weight: "fill" },
  disabled:     { label: "Apagado",           icon: Circle,       color: "#6B6B6B", bg: "#F5F5F5", border: "#DDDDDD", weight: "regular" },
};

export function StatusPill({ status, label, size = "md", testid, hint }) {
  const meta = STATUS_META[status] || STATUS_META.not_configured;
  const Icon = meta.icon;
  const isSm = size === "sm";
  return (
    <span
      className={`inline-flex items-center gap-1.5 font-semibold uppercase tracking-wide ${
        isSm ? "text-[10px] px-2 py-0.5" : "text-[11px] px-2.5 py-1"
      }`}
      style={{ color: meta.color, backgroundColor: meta.bg, border: `1px solid ${meta.border}` }}
      data-testid={testid || `status-${status}`}
      title={hint || meta.label}
    >
      <Icon size={isSm ? 11 : 13} weight={meta.weight} className={meta.spin ? "animate-spin" : ""} />
      {label || meta.label}
    </span>
  );
}

/* ------------------------- Status helper --------------------------------- */
// Pure helper that any caller can use to derive the right status string from
// a (configured?, enabled?, healthy?) triple. Centralises the rules.
export function deriveStatus({
  configured,
  enabled = true,
  healthy = true,
  required = false,
}) {
  if (!configured) return required ? "needs_config" : "not_configured";
  if (!enabled) return "disabled";
  if (!healthy) return "disconnected";
  return "connected";
}
